import { supabase } from "../supabase/client";
import { TaxProfile } from "../../lib/taxCalculatorUtils";

type DbTaxProfile = {
  id: string;
  name: string;
  income_info: any;
  created_at: string;
};

export const TAX_PROFILES_CHANGED_EVENT = "taxProfiles:changed";

export const notifyTaxProfilesChanged = () => {
  window.dispatchEvent(new Event(TAX_PROFILES_CHANGED_EVENT));
};

export const onTaxProfilesChanged = (handler: () => void) => {
  window.addEventListener(TAX_PROFILES_CHANGED_EVENT, handler);
  return () => window.removeEventListener(TAX_PROFILES_CHANGED_EVENT, handler);
};

const mapDbToTaxProfile = (row: DbTaxProfile): TaxProfile => {
  return {
    id: row.id,
    name: row.name,
    incomeInfo: row.income_info,
    createdAt: Number.isFinite(Date.parse(row.created_at))
      ? Date.parse(row.created_at)
      : Date.now(),
  };
};

export const taxProfilesDB = {
  getAll: async (): Promise<TaxProfile[]> => {
    const { data, error } = await supabase
      .from("tax_profiles")
      .select("id,name,income_info,created_at")
      .order("created_at", { ascending: true });
    if (error) {
      console.error("[taxProfilesDB] getAll failed", error);
      return [];
    }
    return (data || []).map(mapDbToTaxProfile);
  },

  upsert: async (profile: TaxProfile): Promise<TaxProfile | null> => {
    const { data, error } = await supabase
      .from("tax_profiles")
      .upsert(
        [
          {
            id: profile.id,
            name: profile.name,
            income_info: profile.incomeInfo,
          },
        ],
        { onConflict: "id" }
      )
      .select("id,name,income_info,created_at")
      .single();
    if (error) {
      console.error("[taxProfilesDB] upsert failed", error);
      return null;
    }
    notifyTaxProfilesChanged();
    return mapDbToTaxProfile(data as DbTaxProfile);
  },

  upsertMany: async (profiles: TaxProfile[]): Promise<TaxProfile[]> => {
    if (profiles.length === 0) return [];
    const payload = profiles.map((p) => ({
      id: p.id,
      name: p.name,
      income_info: p.incomeInfo,
    }));
    const { data, error } = await supabase
      .from("tax_profiles")
      .upsert(payload, { onConflict: "id" })
      .select("id,name,income_info,created_at");
    if (error) {
      console.error("[taxProfilesDB] upsertMany failed", error);
      return [];
    }
    notifyTaxProfilesChanged();
    return (data || []).map(mapDbToTaxProfile);
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase.from("tax_profiles").delete().eq("id", id);
    if (error) {
      console.error("[taxProfilesDB] delete failed", error);
      return false;
    }
    notifyTaxProfilesChanged();
    return true;
  },
};
